import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ARROW_START_MS,
  BG_DURATION_MS,
  CLOSE_FADE_MS,
  CLOSE_REFLOW_MS,
  INNER_START_MS,
  OPEN_AT_MS,
  PUSH_HOLD_MS,
  TEXT_START_MS,
} from '@/views/hexView/expandAnim'

export type ExpansionPhase =
  | 'push'
  | 'bg_in'
  | 'inner'
  | 'text'
  | 'arrow'
  | 'open'
  | 'closing'
  | 'reflow'

/** Absolute delays from expand open (ms). */
const OPEN_PHASE_TIMINGS: Array<{ phase: ExpansionPhase; delayMs: number }> = [
  { phase: 'push',  delayMs: 0 },
  { phase: 'bg_in', delayMs: PUSH_HOLD_MS },
  { phase: 'inner', delayMs: PUSH_HOLD_MS + INNER_START_MS },
  { phase: 'text',  delayMs: PUSH_HOLD_MS + TEXT_START_MS },
  { phase: 'arrow', delayMs: PUSH_HOLD_MS + ARROW_START_MS },
  { phase: 'open',  delayMs: OPEN_AT_MS },
]

interface ExpansionState {
  expandedId: string | null
  phase: ExpansionPhase
  open: (id: string) => void
  close: () => void
  toggle: (id: string) => void
}

/**
 * Expand / close state machine.
 *
 * Close is a two-step, single-motion sequence so nothing snaps or flies:
 *
 *   1. `closing`  — crossfade the expanded chrome out while the layered hex
 *                   fades in. The grid layout is FROZEN at the current
 *                   expansion (expandedId is kept), so no hex moves yet.
 *   2a. switch    — jump straight to the next expansion. The layout goes
 *                   directly from old-expanded -> new-expanded in ONE reflow
 *                   (no intermediate fully-collapsed target, so no fly-back).
 *   2b. pure close — collapse the expanded slot back to a single hex in one
 *                    smooth reflow, then idle.
 */
export function useHexExpansion(): ExpansionState {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [phase, setPhase] = useState<ExpansionPhase>('push')
  const timersRef = useRef<number[]>([])
  const pendingOpenRef = useRef<string | null>(null)
  const expandedIdRef = useRef<string | null>(null)

  useEffect(() => {
    expandedIdRef.current = expandedId
  }, [expandedId])

  const clearTimers = useCallback(() => {
    for (const id of timersRef.current) window.clearTimeout(id)
    timersRef.current = []
  }, [])

  const scheduleOpenPhases = useCallback(() => {
    for (const { phase: p, delayMs } of OPEN_PHASE_TIMINGS.slice(1)) {
      const t = window.setTimeout(() => setPhase(p), delayMs)
      timersRef.current.push(t)
    }
  }, [])

  const open = useCallback((id: string) => {
    clearTimers()
    pendingOpenRef.current = null
    setExpandedId(id)
    setPhase('push')
    scheduleOpenPhases()
  }, [clearTimers, scheduleOpenPhases])

  const close = useCallback(() => {
    if (!expandedIdRef.current) {
      pendingOpenRef.current = null
      return
    }
    clearTimers()
    // Step 1: crossfade only. Keep expandedId so the layout stays put.
    setPhase('closing')
    const t = window.setTimeout(() => {
      const next = pendingOpenRef.current
      pendingOpenRef.current = null
      if (next) {
        // Step 2a: go directly to the next expansion — single smooth reflow.
        open(next)
      } else {
        // Step 2b: collapse the gap in one reflow, then idle.
        setExpandedId(null)
        setPhase('reflow')
        const t2 = window.setTimeout(() => setPhase('push'), CLOSE_REFLOW_MS)
        timersRef.current.push(t2)
      }
    }, CLOSE_FADE_MS)
    timersRef.current.push(t)
  }, [clearTimers, open])

  const toggle = useCallback((id: string) => {
    const current = expandedIdRef.current
    if (!current) {
      open(id)
      return
    }
    if (current === id) {
      pendingOpenRef.current = null
      close()
      return
    }
    // Switching tiles: remember the target, then run the close crossfade first.
    pendingOpenRef.current = id
    close()
  }, [open, close])

  useEffect(() => () => clearTimers(), [clearTimers])

  return { expandedId, phase, open, close, toggle }
}

/** Collapsed stack fade duration — matches a single background hex fade-in. */
export const COLLAPSED_FADE_MS = BG_DURATION_MS
